import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  StatusDot,
  Tabs,
  Text,
  useToast,
  type TabItem,
} from '@teals/ui-web';
import {
  useCloneSessionMutation,
  useGetAgentQuery,
  useGetAnalyticsSessionQuery,
  useGetSessionHistoryQuery,
} from '../../store/api';
import {
  approxDuration,
  deriveSource,
  deriveStatus,
  formatSessionId,
  groupExchanges,
  relativeTime,
  sourceLabel,
  statusLabel,
} from '../AgentDetail/sessionHelpers';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 767px) {
    padding: 1.5rem 1rem;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  width: fit-content;

  &:hover {
    color: var(--accent-hover);
  }
`;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const SubMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SummaryBanner = styled.div<{ $failed?: boolean }>`
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 1px solid
    ${({ $failed }) => ($failed ? 'var(--error, #dc2626)' : 'var(--border)')};
  background: ${({ $failed }) =>
    $failed ? 'rgba(220, 38, 38, 0.06)' : 'var(--bg-elevated, var(--bg-surface))'};
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin-bottom: 0.35rem;
`;

const StaffBanner = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: var(--bg-elevated, rgba(15, 23, 42, 0.04));
`;

const LaneTabsMobile = styled.div`
  display: none;

  @media (max-width: 1023px) {
    display: block;
  }
`;

const ReplayLayout = styled.div<{ $activeLane: string }>`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 1rem;
  min-height: 480px;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;

    [data-lane] {
      display: none;
    }

    [data-lane='${({ $activeLane }) => $activeLane}'] {
      display: flex;
    }
  }
`;

const Lane = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-surface);
  min-height: 420px;
`;

const LaneHeader = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border-bottom: 1px solid var(--border);
  background: var(--bg-elevated, var(--bg-surface));
`;

const LaneBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ExchangeDivider = styled.div`
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  margin: 0.5rem 0;
`;

const MessageBubble = styled.div<{ $role: string }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 92%;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) => ($role === 'user' ? '#fff' : 'var(--text-primary)')};
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MsgMeta = styled.div`
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const LANE_TABS: TabItem[] = [
  { id: 'conversation', label: 'Conversation' },
  { id: 'thinking', label: 'Thinking' },
  { id: 'actions', label: 'Actions' },
];

function formatMessageContent(content: string): string {
  const trimmed = content.trim();
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return content;
    }
  }
  return content;
}

const SessionReplay: React.FC = () => {
  const navigate = useNavigate();
  const { id = '', sid = '' } = useParams<{ id: string; sid: string }>();
  const toast = useToast();
  const { show } = toast;
  const [activeLane, setActiveLane] = useState('conversation');

  const { data: agent } = useGetAgentQuery(id, { skip: !id });
  const {
    data: history,
    isLoading,
    isError,
  } = useGetSessionHistoryQuery(sid, { skip: !sid });
  const { data: analyticsSpot } = useGetAnalyticsSessionQuery(sid, { skip: !sid });
  const [cloneSession, { isLoading: cloning }] = useCloneSessionMutation();

  const source = deriveSource(history?.metadata);
  const status = deriveStatus(history?.metadata);
  const exchanges = useMemo(
    () => groupExchanges(history?.messages ?? []),
    [history?.messages],
  );

  const openPlayground = (sessionId: string) => {
    navigate(`/agents/${id}?tab=playground&sessionId=${encodeURIComponent(sessionId)}`);
  };

  const handleClone = async () => {
    try {
      const result = await cloneSession(sid).unwrap();
      show?.({ message: 'Session cloned into Playground', type: 'success' });
      openPlayground(result.sessionId);
    } catch {
      show?.({ message: 'Failed to clone session', type: 'error' });
    }
  };

  const handleExport = () => {
    const payload = {
      sessionId: sid,
      agentName: history?.agentName,
      metadata: history?.metadata,
      messages: history?.messages ?? [],
      note: 'Thinking/actions trail not included — not persisted yet',
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-${sid}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Page>
        <Text variant="body1">Loading session…</Text>
      </Page>
    );
  }

  if (isError || !history) {
    return (
      <Page>
        <BackLink onClick={() => navigate(`/agents/${id}?tab=sessions`)}>
          &larr; Back to Sessions
        </BackLink>
        <EmptyState
          title="Session not found"
          description="This session may not belong to your organisation or no longer exists."
        />
      </Page>
    );
  }

  return (
    <Page data-testid="SessionReplay">
      <BackLink onClick={() => navigate(`/agents/${id}?tab=sessions`)}>
        &larr; Back to Sessions
      </BackLink>

      <Header>
        <HeaderMeta>
          <TitleRow>
            <Text variant="h3" style={{ margin: 0 }}>
              {agent?.name || history.agentName}
            </Text>
            <Text
              variant="body1"
              style={{
                margin: 0,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                color: 'var(--text-secondary)',
              }}
            >
              {formatSessionId(sid, 12)}
            </Text>
            <StatusDot
              status={
                status === 'failed'
                  ? 'error'
                  : status === 'success'
                    ? 'active'
                    : 'draft'
              }
              label={statusLabel(status)}
            />
            <Badge variant={source === 'api' ? 'warning' : 'info'} size="sm">
              {sourceLabel(source)}
              {history.metadata?.applicationId
                ? ` · ${history.metadata.applicationId}`
                : ''}
            </Badge>
          </TitleRow>
          <SubMeta>
            <span>{relativeTime(history.updatedAt || history.createdAt)}</span>
            <span>{exchanges.length} exchanges</span>
            <span>{history.messages.length} messages</span>
            <span>{approxDuration(history.createdAt, history.updatedAt)}</span>
            {analyticsSpot?.tokensTotal != null && (
              <span>{analyticsSpot.tokensTotal.toLocaleString()} tokens</span>
            )}
            <span>continuous trail</span>
          </SubMeta>
        </HeaderMeta>
        <Actions>
          <Button variant="primary" size="sm" disabled={cloning} onClick={handleClone}>
            {cloning ? 'Cloning…' : 'Clone'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => openPlayground(sid)}>
            Open in Playground
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExport}>
            Export
          </Button>
        </Actions>
      </Header>

      <SummaryBanner $failed={status === 'failed'}>
        <SummaryLabel>
          {status === 'failed' ? 'What went wrong' : 'Session summary'}
        </SummaryLabel>
        <Text variant="body1" style={{ margin: 0 }}>
          {status === 'failed'
            ? 'Failure-first summary unavailable — exchange error trail is not persisted yet. Review the conversation lane for the continuous chat trail.'
            : 'Summary unavailable — trail not enriched yet. Conversation below is the durable multi-turn history.'}
        </Text>
      </SummaryBanner>

      <StaffBanner>
        Full detail · staff — role-aware redaction not enforced yet (Phase 2 hook).
      </StaffBanner>

      <LaneTabsMobile data-testid="LaneTabs">
        <Tabs
          items={LANE_TABS}
          activeTab={activeLane}
          onChange={setActiveLane}
          variant="underline"
        />
      </LaneTabsMobile>

      <ReplayLayout $activeLane={activeLane}>
        <Lane data-lane="conversation">
          <LaneHeader>Conversation</LaneHeader>
          <LaneBody>
            {exchanges.length === 0 && (
              <EmptyState
                title="No messages"
                description="This session has no stored conversation history."
              />
            )}
            {exchanges.map((group, idx) => (
              <React.Fragment key={`ex-${idx}`}>
                <ExchangeDivider>Exchange {idx + 1}</ExchangeDivider>
                {group.map((msg, mIdx) => (
                  <div key={`${idx}-${mIdx}`}>
                    <MessageBubble $role={msg.role}>
                      {formatMessageContent(msg.content)}
                    </MessageBubble>
                    {msg.timestamp && (
                      <MsgMeta>{relativeTime(msg.timestamp)}</MsgMeta>
                    )}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </LaneBody>
        </Lane>

        <Lane data-lane="thinking">
          <LaneHeader>Thinking</LaneHeader>
          <LaneBody>
            <EmptyState
              title="Thinking trail not persisted yet"
              description="Live Playground WebSocket steps are not stored for historical replay. Phase 2 will persist a continuous thinking trail."
            />
          </LaneBody>
        </Lane>

        <Lane data-lane="actions">
          <LaneHeader>Actions</LaneHeader>
          <LaneBody>
            <EmptyState
              title="Actions trail not persisted yet"
              description="Task and exchange events are not stored for historical replay. Phase 2 will persist a continuous actions trail."
            />
          </LaneBody>
        </Lane>
      </ReplayLayout>
    </Page>
  );
};

export default SessionReplay;
